import React, { useState, useEffect } from 'react';
import { apiClient } from '../api';
import { Modal, Chip, Fold, primaryButton } from '../components/ui';
import { notReceiving, splitFailures, versionBreakdown } from '../lib/publishReport';
import { MAX_NAMED, andList, plural } from '../lib/text';

export { plural };

// Le pregunta al servidor qué pasaría en cada una de esas versiones. Devuelve [detalle, reintentar]: el detalle es
// undefined mientras consulta y null si no se pudo. `onLoaded` recibe la respuesta cuando llega.
export const usePublishPreview = (filenames, clients, versions, onLoaded) => {
    const [preview, setPreview] = useState(undefined);
    const [attempt, setAttempt] = useState(0);
    useEffect(() => {
        let alive = true;
        apiClient.post('/api/publish_preview', { filenames, clients, versions })
            .then(res => {
                if (!alive) return;
                const loaded = Array.isArray(res.data?.versions) ? res.data : null;
                setPreview(loaded);
                if (loaded) onLoaded?.(loaded);
            })
            .catch(() => { if (alive) setPreview(null); });
        return () => { alive = false; };
        // El cartel se abre para una selección fija: se consulta una sola vez, salvo que se pida reintentar.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [attempt]);
    const retry = () => { setPreview(undefined); setAttempt(n => n + 1); };
    return [preview, retry];
};

const Line = ({ label, tone = 'text-slate-600', children }) => (
    <p className={`text-xs ${tone}`}>{label && <><span className="font-semibold">{label}</span>{' '}</>}{children}</p>
);
const Names = ({ names }) => <span className="font-mono">{names.join(', ')}</span>;
const has = (names, one, many) => `${andList(names)} ${names.length === 1 ? one : many}`;

// Lo que le pasa a UN update en una versión. Lo que sale mal va en rojo y a la vista; lo esperado (el update es de
// otro cliente) va en gris y plegado; las listas de más de cinco clientes se cuentan y se abren con "ver detalle".
const FileLines = ({ file, done }) => {
    const { published, alreadyPublished, ignored, notApplicable, failures } = file;
    const long = (names) => names.length > MAX_NAMED;
    return (
        <>
            {published.length > 0 && (
                <Line label={done ? 'Publicado para' : 'Se publica para'} tone="text-emerald-800">
                    {long(published) ? <>{plural(published.length, 'cliente', 'clientes')} <Fold about={`${file.filename}, a quién se publica`}><Names names={published} /></Fold></> : <Names names={published} />}
                </Line>
            )}
            {alreadyPublished.length > 0 && (
                <Line>
                    {long(alreadyPublished)
                        ? <>{alreadyPublished.length} ya lo {done ? 'tenían' : 'tienen'} <Fold about={`${file.filename}, quién ya lo tiene`}><Names names={alreadyPublished} /></Fold></>
                        : `${has(alreadyPublished, done ? 'ya lo tenía' : 'ya lo tiene', done ? 'ya lo tenían' : 'ya lo tienen')}.`}
                </Line>
            )}
            {ignored.length > 0 && <Line>{has(ignored, 'lo tiene ignorado', 'lo tienen ignorado')}.</Line>}
            {notApplicable && (
                <Line tone="text-slate-500">
                    {notReceiving(notApplicable.clients.length, 1, done)}{' '}
                    <Fold about={`${file.filename}, a quién no le corresponde`}>No {notApplicable.clients.length === 1 ? 'le' : 'les'} corresponde, es de {notApplicable.owner}: <Names names={notApplicable.clients} /></Fold>
                </Line>
            )}
            {failures.map(failure => (
                <Line key={failure.text} label={done ? 'No se publicó' : 'No se publica'} tone="text-red-700">
                    {failure.clients.length > 0 && <>para <Names names={failure.clients} />: </>}{failure.text}
                </Line>
            ))}
        </>
    );
};

// Qué pasa (o qué pasó, con `done`) en cada versión: cada una con su zip, sus clientes y sus prerrequisitos.
export const VersionBreakdown = ({ report, versions, filenames, hashes = {}, done = false }) => (
    <div className="space-y-2">
        {versions.map(version => {
            const detail = versionBreakdown(report, version, filenames);
            const nothing = detail.published + detail.notApplicable + detail.failed === 0;
            return (
                <section key={version} aria-label={`Versión ${version}`} className="border border-slate-900/10 rounded-lg overflow-hidden">
                    <p className="flex items-center justify-between gap-2 px-3 py-1.5 bg-slate-50 border-b border-slate-200">
                        <span className="font-mono text-[13px] font-semibold text-slate-800">{version}</span>
                        {/* El rojo es sólo para lo que sale mal: que un update de otro cliente no le llegue a éste es lo esperado. */}
                        <span className="text-xs font-medium text-slate-500">
                            {[
                                detail.published > 0 && <span key="published">{done ? plural(detail.published, 'publicada', 'publicadas') : plural(detail.published, 'publicación', 'publicaciones')}</span>,
                                detail.notApplicable > 0 && <span key="expected">{detail.notApplicable} no lo {detail.notApplicable === 1 ? (done ? 'recibió' : 'recibe') : (done ? 'recibieron' : 'reciben')}</span>,
                                detail.failed > 0 && <span key="failed" className="text-red-700">{detail.failed} con error</span>,
                            ].filter(Boolean).flatMap((part, i) => i === 0 ? [part] : [' · ', part])}
                            {nothing && (done ? 'Nada publicado' : 'Nada para publicar')}
                        </span>
                    </p>
                    <div className="px-3 py-2 space-y-2">
                        {detail.files.map(file => (
                            <div key={file.filename} className="space-y-0.5">
                                {(filenames.length > 1 || hashes[version]?.[file.filename]) && (
                                    <p className="flex items-center gap-2">
                                        <span className="font-mono text-xs font-medium text-slate-800 truncate">{file.filename}</span>
                                        {hashes[version]?.[file.filename] && <Chip title={`Hash del zip de ${version}: ${hashes[version][file.filename]}`}>zip {hashes[version][file.filename].slice(0, 8)}</Chip>}
                                    </p>
                                )}
                                <FileLines file={file} done={done} />
                            </div>
                        ))}
                        {detail.dependencies.map(group => (
                            <Line key={group.text + group.clients.join()} label={done ? 'Con sus prerrequisitos:' : 'Antes, sus prerrequisitos:'} tone="text-amber-800">
                                <Names names={[group.text]} /> (para {group.clients.join(', ')})
                            </Line>
                        ))}
                    </div>
                </section>
            );
        })}
    </div>
);

// Después de liberar en varias versiones: qué se publicó y qué no en cada una.
export default function PublishResultDialog({ report, versions, filenames, onClose }) {
    const successes = (report.successes || []).length;
    const { expected, failed } = splitFailures(report.failures);
    return (
        <Modal title="Resultado de la liberación" onClose={onClose} footer={<button type="button" onClick={onClose} className={primaryButton}>Entendido</button>}>
            <p>
                <strong>{plural(successes, 'publicación hecha', 'publicaciones hechas')}</strong> en {plural(versions.length, 'versión', 'versiones')}
                {expected.length > 0 && <span className="text-slate-500">, {expected.length} no lo {expected.length === 1 ? 'recibió' : 'recibieron'}</span>}
                {failed.length > 0 && <>, y <strong className="text-red-700">{failed.length} con error</strong></>}.
            </p>
            <VersionBreakdown report={report} versions={versions} filenames={filenames} done />
        </Modal>
    );
}
